import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { ProfilePageDocument, UpdateEmployeeDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Form from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import Select from 'modules/core/components/Inputs/Select'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { BusinessError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import TimeoffForm from 'modules/employees/lib/forms/EmployeeForm/Timeoff'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'

const createTimeoffForm = (
  timeoffManager: { id: string; fullName: string } | null,
  timeoffPolicyId: string | null,
  tenureStartDate: string | null
) => {
  const effectivePolicyId = hasPolicyTemporality() ? null : timeoffPolicyId

  return new TimeoffForm({
    timeoff_manager_id: timeoffManager?.id ?? undefined,
    timeoff_policy_id: effectivePolicyId ?? undefined,
    tenure_start_date: tenureStartDate ?? undefined,
  })
}

interface Props {
  employeeId: string
  accessId: string
  employeeFullName: string
  timeoffManager: { id: string; fullName: string } | null
  timeoffPolicyId: string | null
  tenureStartDate: string | null
  companyId: string
  canEditManager: boolean
  readonly?: boolean
}

const TimeoffManagerSelector = ({
  employeeId,
  accessId,
  employeeFullName: _employeeFullName,
  timeoffManager,
  timeoffPolicyId,
  tenureStartDate,
  companyId,
  canEditManager,
  readonly,
}: Props) => {
  const [timeoffForm, setTimeoffForm] = useState<TimeoffForm>(
    createTimeoffForm(timeoffManager, timeoffPolicyId, tenureStartDate)
  )
  const [selectedTimeoffManagerId, setSelectedTimeoffManagerId] = useState<string | null>(
    timeoffManager?.id ?? null
  )
  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
    refetchQueries: [ProfilePageDocument],
  })

  useEffect(() => {
    setTimeoffForm(createTimeoffForm(timeoffManager, timeoffPolicyId, tenureStartDate))
    setSelectedTimeoffManagerId(timeoffManager?.id ?? null)
  }, [timeoffManager?.id, timeoffPolicyId, tenureStartDate])

  const isReadonly = readonly || !canEditManager

  const options = timeoffPolicies.models.map((timeoffPolicy) => ({
    label: timeoffPolicy.attributes.name,
    value: timeoffPolicy.attributes.id,
  }))

  function onSubmit() {
    track(trackingEvents.employee.profile.editEmployeeProfileTimeOffSubmit)
    return asyncAction(
      async () => {
        const result = await updateEmployee({
          variables: {
            id: employeeId,
            accessId: accessId,
            ...(timeoffForm.get('timeoff_manager_id').isDirty && {
              timeoffManagerId: timeoffForm.get('timeoff_manager_id').value || null,
            }),
            ...(!hasPolicyTemporality() &&
              timeoffForm.get('timeoff_policy_id').isDirty && {
                timeoffPolicyId: timeoffForm.get('timeoff_policy_id').value
                  ? String(timeoffForm.get('timeoff_policy_id').value)
                  : undefined,
              }),
          },
        })

        const unmatchedErrors: string[] = []

        const handledResult = handleResult(result, (errors) => {
          Object.entries(errors).forEach(([key, message]) => {
            if (timeoffForm.has(key)) {
              timeoffForm.get(key).setErrors([message])
            } else {
              unmatchedErrors.push(message)
            }
          })
        })

        if (!handledResult.success) {
          if (unmatchedErrors.length > 0) {
            app.setFlash({
              type: 'wrong',
              message: unmatchedErrors.join(', '),
              time: 5000,
            })
          }
          throw new BusinessError(
            `UpdateEmployee mutation failed while updating time off settings for employee ${employeeId}${
              unmatchedErrors.length ? `: ${unmatchedErrors.join(', ')}` : ''
            }`
          )
        }
      },
      { errorFlash: false }
    )
  }

  return (
    <Form onSubmit={onSubmit}>
      {!hasPolicyTemporality() && canEditManager && (
        <FormRow>
          <Select
            field={timeoffForm.get('timeoff_policy_id')}
            label={i18n.t('time_off.timeoff_policy_legend')}
            options={options}
            readonly
          />
        </FormRow>
      )}
      <FormRow>
        <EmployeeSelectorV2
          label={i18n.t('time_off.timeoff_manager_legend')}
          disabled={isReadonly}
          forcedFilters={{ companyId, excludeIds: [String(employeeId)] }}
          value={selectedTimeoffManagerId ?? undefined}
          onChange={(timeOffManagerId) => {
            setSelectedTimeoffManagerId(timeOffManagerId ?? null)
            timeoffForm
              .get('timeoff_manager_id')
              .set(timeOffManagerId ? String(timeOffManagerId) : null)
          }}
        />
      </FormRow>
      {hasFeature('tenure_start_date') && (
        <FormRow>
          <DayPicker
            readonly={isReadonly}
            field={timeoffForm.get('tenure_start_date')}
            label={i18n.t('time_off.tenure_start_date')}
            placeholder={i18n.t('time_off.contract_start_date')}
            withNullLabel={i18n.t('time_off.use_contract_start_date')}
            withNull
          />
        </FormRow>
      )}
      {timeoffForm.isDirty && (
        <FormRow footer>
          <Button submit>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </Form>
  )
}

export default observer(TimeoffManagerSelector)
